import React from 'react';
import './MarketBandTooltip.css';

// Human labels for the provider-type segment shown in the market-band tooltip.
const PROVIDER_LABELS = {
  CRNA: 'CRNA',
  Anesthesiologist: 'Anesthesiologist',
  CAA: 'CAA',
  anesthesia: 'Anesthesia',
};

const fmtHourly = (n) => `$${Math.round(n)}`;
const fmtAnnual = (n) => `$${Math.round(n / 1000)}k`;

/**
 * One pay-unit row (hourly or salary): a label, the p25..p75 range, and the
 * median. Renders nothing when that unit has no band, so a segment can show one
 * row or both.
 */
const UnitRow = ({ label, band, unit }) => {
  if (!band) return null;
  const fmt = unit === 'annual' ? fmtAnnual : fmtHourly;
  const suffix = unit === 'annual' ? '/yr' : '/hr';
  return (
    <div className="mbt__row">
      <span className="mbt__rowlabel">{label}</span>
      <span className="mbt__rowbody">
        <span className="mbt__range">
          <span className="mbt__amt">{fmt(band.p25)}</span>
          <span className="mbt__to">to</span>
          <span className="mbt__amt">{fmt(band.p75)}</span>
          <span className="mbt__unit">{suffix}</span>
        </span>
        <span className="mbt__med">
          Median {fmt(band.median)}
          {suffix}
        </span>
      </span>
    </div>
  );
};

/**
 * MarketBandTooltip — the styled content revealed when a provider taps the
 * unified "Competitive Rate" badge on a listing whose rate is not shown.
 *
 * It frames the numbers as the MARKET range for the listing's segment
 * (state x provider type x assignment), never as this listing's confirmed rate,
 * and shows the hourly and/or salary band, whichever the backend resolved.
 *
 * Backward compatible: a legacy flat band ({ p25, median, p75, tier }) is treated
 * as the hourly band, so the tooltip is correct whether the backend sends the
 * old single-band shape or the new { hourly, annual } shape.
 */
const MarketBandTooltip = ({ providerType, state, canonicalCategory, band }) => {
  if (!band) return null;

  const hourly = band.hourly ?? (typeof band.p25 === 'number' ? band : null);
  const annual = band.annual ?? null;
  if (!hourly && !annual) return null;

  const typeLabel = PROVIDER_LABELS[providerType] || PROVIDER_LABELS.anesthesia;
  const loc = state ? String(state).toUpperCase() : 'Your area';

  // Name the assignment only when a band resolved at the category-specific tier;
  // otherwise the range is not category-specific and naming it would imply a
  // precision the number does not have.
  const tierCell = hourly || annual;
  const assignment =
    tierCell.tier === 'state_type_category' && canonicalCategory
      ? String(canonicalCategory).replace(/_/g, ' ')
      : '';

  return (
    <div className="mbt">
      <div className="mbt__head">
        <span className="mbt__eyebrow">Market rate</span>
        <span className="mbt__loc">{loc}</span>
      </div>
      <div className="mbt__seg">
        {typeLabel}
        {assignment ? ` · ${assignment}` : ''}
      </div>

      <UnitRow label="Hourly" band={hourly} unit="hourly" />
      <UnitRow label="Salary" band={annual} unit="annual" />

      <div className="mbt__note">
        Typical pay for this market, not this listing&apos;s confirmed rate.
      </div>
    </div>
  );
};

export default MarketBandTooltip;
